import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, LogOut, ScanLine, Search, Baby, LogIn, LogOut as LogOutIcon, AlertTriangle, CheckCircle2, Camera, Upload } from "lucide-react";
import { Logo } from "../components/Logo";
import { PhotoThumb } from "../components/ImageLightbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, apiError, fileUrl } from "../lib/api";
import { toast } from "sonner";

const ALLOWED_ROLES = ["admin", "welcome_coordinator", "child_care_staff"];

// html5-qrcode deja promesas play() sin capturar al pausar/detener la cámara (benigno).
if (typeof window !== "undefined" && !window.__hzPlayPatched) {
  window.__hzPlayPatched = true;
  const origPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function (...args) {
    const p = origPlay.apply(this, args);
    if (p && typeof p.catch === "function") return p.catch(() => {});
    return p;
  };
}

const ChildPhotoUpload = ({ childId, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(`/admin/children/${childId}/photo`, fd);
      toast.success(data.child_care_eligible
        ? "Fotografía verificada: el niño ya es elegible para cuidado infantil."
        : "Fotografía verificada, pero el niño aún no es elegible (revisa los adultos autorizados).");
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-hz-gray/40 bg-white p-2" data-testid={`care-photo-upload-box-${childId}`}>
      <input type="file" accept="image/*" capture="user" className="hidden" ref={camRef}
        data-testid={`care-child-photo-camera-input-${childId}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }} />
      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" ref={galRef}
        data-testid={`care-child-photo-input-${childId}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }} />
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="outline" onClick={() => camRef.current?.click()} data-testid={`care-child-photo-camera-btn-${childId}`} className="text-xs font-bold">
          <Camera className="mr-1 h-3.5 w-3.5" /> Tomar foto
        </Button>
        <Button size="sm" variant="outline" onClick={() => galRef.current?.click()} data-testid={`care-child-photo-gallery-btn-${childId}`} className="text-xs font-bold">
          <Upload className="mr-1 h-3.5 w-3.5" /> Subir imagen
        </Button>
      </div>
      <p className="text-xs text-hz-ink/50">En algunos dispositivos, la cámara puede abrirse mediante el selector del sistema.</p>
      {preview && <img src={preview} alt="Vista previa" className="h-16 w-16 rounded-lg object-cover" data-testid={`care-child-photo-preview-${childId}`} />}
      {file && (
        <Button size="sm" onClick={upload} disabled={busy} data-testid={`care-child-photo-upload-${childId}`} className="bg-hz-cyan font-bold text-white">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Guardar y validar foto
        </Button>
      )}
    </div>
  );
};

const ChildCarePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("check_in");
  const [inputMode, setInputMode] = useState("scan");
  const [tokenInput, setTokenInput] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [scannedType, setScannedType] = useState("manual_lookup");
  const [selected, setSelected] = useState({});
  const [receiver, setReceiver] = useState("");
  const [notes, setNotes] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const idemKeys = useRef({});
  const scannerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user || inputMode !== "scan" || !ALLOWED_ROLES.includes(user.role)) return;
    const scanner = new Html5Qrcode("care-qr-reader");
    scannerRef.current = scanner;
    let active = true;
    let started = false;
    scanner.start(
      { facingMode: "environment" },
      { fps: 8, qrbox: { width: 240, height: 240 } },
      (text) => { if (active) resolveToken(text); },
      () => {}
    ).then(() => { started = true; }).catch(() => {});
    return () => {
      active = false;
      if (!started) return;
      try {
        scanner.stop().then(() => scanner.clear()).catch(() => {});
      } catch (e) {
        // el escáner no llegó a iniciarse
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, inputMode]);

  const resolveToken = async (token) => {
    try {
      const { data } = await api.get("/child-care/resolve", { params: { token } });
      if (data.kind === "group") {
        setFicha(data);
        setScannedType("family_care_qr");
      } else if (data.kind === "adult") {
        if (data.fichas.length === 1) {
          setFicha({ ...data.fichas[0], presenter: data.presenter });
        } else {
          setSearchResults(data.fichas.map((f) => ({ ...f.group, children_count: f.children.length, presenter: data.presenter })));
          setInputMode("search");
        }
        setScannedType("adult_qr");
      }
      setSelected({});
      setFeedback(null);
    } catch (err) {
      toast.error(apiError(err, "Código no reconocido."));
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get("/child-care/resolve", { params: { q: searchQ } });
      setSearchResults(data.results || []);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openGroup = async (groupId) => {
    try {
      const { data } = await api.get(`/child-care/groups/${groupId}`);
      setFicha(data);
      setScannedType("manual_lookup");
      setSelected({});
      setSearchResults(null);
      setFeedback(null);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleChild = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const receiverOptions = () => {
    const map = new Map();
    selectedIds.forEach((cid) => {
      const child = ficha?.children.find((c) => c.id === cid);
      (child?.authorized_guardians || []).forEach((g) => {
        const key = g.attendee_id ? `attendee:${g.attendee_id}` : `guardian:${g.id}`;
        if (!map.has(key)) map.set(key, { key, label: `${g.name}${g.relationship ? ` (${g.relationship})` : ""}`, attendee_id: g.attendee_id || null, guardian_id: g.attendee_id ? null : g.id });
      });
    });
    return [...map.values()];
  };

  const submit = async () => {
    if (!ficha || busy || selectedIds.length === 0) return;
    if (mode === "check_out" && !window.confirm("¿Confirmas la entrega de los niños seleccionados a la persona indicada?")) return;
    setBusy(true);
    setFeedback(null);
    const errs = [];
    let done = 0;
    for (const cid of selectedIds) {
      if (!idemKeys.current[cid]) idemKeys.current[cid] = crypto.randomUUID();
      const payload = {
        child_id: cid, action: mode, scanned_qr_type: scannedType,
        notes: notes || null, idempotency_key: idemKeys.current[cid],
        presented_by_attendee_id: ficha.presenter?.id || null,
      };
      if (mode === "check_out" && receiver) {
        const [kind, rid] = receiver.split(":");
        if (kind === "attendee") payload.released_to_attendee_id = rid;
        else payload.released_to_guardian_id = rid;
        if (overrideReason.trim()) payload.override_reason = overrideReason.trim();
      }
      try {
        await api.post("/child-care/action", payload);
        done += 1;
        delete idemKeys.current[cid];
      } catch (err) {
        const child = ficha.children.find((c) => c.id === cid);
        errs.push(`${child?.full_name}: ${apiError(err)}`);
      }
    }
    setBusy(false);
    if (errs.length) {
      setFeedback({ ok: false, messages: errs });
    } else {
      setFeedback({ ok: true, messages: [mode === "check_in" ? `${done} entrada(s) registrada(s).` : `${done} salida(s) registrada(s).`] });
    }
    setSelected({});
    setReceiver("");
    setOverrideReason("");
    if (ficha?.group?.id) openGroup(ficha.group.id);
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/login?redirect=/cuidado-infantil" replace />;
  if (!ALLOWED_ROLES.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center" data-testid="care-no-perms">
        <p className="text-lg font-bold text-hz-navy">Tu rol no tiene acceso al módulo de cuidado infantil.</p>
        <Button onClick={() => { localStorage.removeItem("hz_admin_token"); navigate("/login"); }} className="mt-4 bg-hz-navy font-bold text-white" data-testid="care-relogin-btn">Cerrar sesión</Button>
      </div>
    );
  }

  const isManager = ["admin", "welcome_coordinator"].includes(user.role);
  const candidates = ficha ? ficha.children.filter((c) => (mode === "check_in" ? c.care_status !== "checked_in" : c.care_status === "checked_in")) : [];

  return (
    <div className="min-h-screen bg-hz-bg" data-testid="childcare-page">
      <header className="border-b border-hz-gray/50 bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo variant="title" className="h-8" />
            <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-bold text-hz-navy">Cuidado infantil</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-medium text-hz-ink/70 sm:block">{user.email}</span>
            {isManager && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin/cuidado-infantil")} data-testid="care-dashboard-link" className="font-semibold">Panel</Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("hz_admin_token"); navigate("/login"); }} data-testid="care-logout-btn" className="font-semibold text-hz-error">
              <LogOut className="mr-1 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => { setMode("check_in"); setSelected({}); }} data-testid="care-mode-checkin"
            className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 font-bold transition-colors ${mode === "check_in" ? "border-hz-cyan bg-hz-cyan/10 text-hz-navy" : "border-hz-gray/40 bg-white text-hz-ink/60"}`}>
            <LogIn className="h-5 w-5" /> Entrada de niños
          </button>
          <button onClick={() => { setMode("check_out"); setSelected({}); }} data-testid="care-mode-checkout"
            className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 font-bold transition-colors ${mode === "check_out" ? "border-hz-cyan bg-hz-cyan/10 text-hz-navy" : "border-hz-gray/40 bg-white text-hz-ink/60"}`}>
            <LogOutIcon className="h-5 w-5" /> Recogida
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={() => setInputMode("scan")} data-testid="care-input-scan"
            className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${inputMode === "scan" ? "bg-hz-navy text-white" : "bg-white text-hz-navy"}`}>
            <ScanLine className="h-4 w-4" /> Escanear QR
          </button>
          <button onClick={() => setInputMode("search")} data-testid="care-input-search"
            className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${inputMode === "search" ? "bg-hz-navy text-white" : "bg-white text-hz-navy"}`}>
            <Search className="h-4 w-4" /> Búsqueda manual
          </button>
        </div>

        {inputMode === "scan" && (
          <div className="mt-4 space-y-3">
            <div id="care-qr-reader" className="overflow-hidden rounded-2xl border border-hz-gray/40 bg-white" data-testid="care-scanner" />
            <div className="flex gap-2">
              <Input value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder="O ingresa el código manualmente" data-testid="care-token-input" />
              <Button onClick={() => resolveToken(tokenInput)} disabled={!tokenInput.trim()} data-testid="care-token-btn" className="bg-hz-navy font-bold text-white">Buscar</Button>
            </div>
          </div>
        )}

        {inputMode === "search" && !ficha && (
          <div className="mt-4 space-y-3">
            <form onSubmit={doSearch} className="flex gap-2">
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Nombre del niño, familia o folio de grupo" data-testid="care-search-input" />
              <Button type="submit" data-testid="care-search-btn" className="bg-hz-navy font-bold text-white"><Search className="h-4 w-4" /></Button>
            </form>
            {searchResults && (
              <div className="space-y-2" data-testid="care-search-results">
                {searchResults.length === 0 && <p className="py-4 text-center text-sm text-hz-ink/60">Sin resultados.</p>}
                {searchResults.map((g) => (
                  <button key={g.id} onClick={() => openGroup(g.id)} data-testid={`care-group-${g.group_folio}`}
                    className="flex w-full items-center justify-between rounded-xl border border-hz-gray/40 bg-white px-4 py-3 text-left hover:border-hz-cyan">
                    <div>
                      <p className="text-sm font-bold text-hz-ink">{g.group_name || g.primary_contact_name}</p>
                      <p className="text-xs text-hz-ink/60">{g.group_folio} · {g.children_count} niño(s)</p>
                    </div>
                    <Baby className="h-4 w-4 text-hz-cyan" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {ficha && (
          <div className="mt-4 space-y-4" data-testid="care-ficha">
            <div className="rounded-2xl border border-hz-gray/40 bg-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-bold text-hz-navy" data-testid="care-ficha-group">{ficha.group.group_name || "Familia"} · {ficha.group.group_folio}</p>
                  <p className="text-xs text-hz-ink/60">Contacto: {ficha.group.primary_contact_name}{ficha.group.primary_contact_phone ? ` · ${ficha.group.primary_contact_phone}` : ""}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setFicha(null); setSelected({}); setFeedback(null); }} data-testid="care-close-ficha" className="font-semibold">Cerrar</Button>
              </div>
              {ficha.presenter && (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-hz-bg p-3" data-testid="care-presenter">
                  {ficha.presenter.photo_url && <PhotoThumb src={ficha.presenter.photo_url} alt={`Fotografía de ${ficha.presenter.full_name}`} className="h-12 w-12 rounded-lg object-cover" testId="care-presenter-photo" />}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-hz-ink/50">Adulto que se presenta</p>
                    <p className="text-sm font-bold text-hz-ink">{ficha.presenter.full_name}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3" data-testid="care-children-list">
              {ficha.children.map((c) => {
                const photoBlocked = !c.child_care_eligible;
                const eligible = mode === "check_in"
                  ? c.care_status !== "checked_in" && !photoBlocked
                  : c.care_status === "checked_in";
                return (
                  <div key={c.id} role="button" tabIndex={eligible ? 0 : -1} onClick={() => eligible && toggleChild(c.id)} data-testid={`care-child-${c.id}`}
                    className={`w-full rounded-2xl border-2 p-4 text-left transition-colors ${selected[c.id] ? "border-hz-cyan bg-hz-cyan/5" : eligible ? "cursor-pointer border-hz-gray/40 bg-white hover:border-hz-cyan/50" : "border-hz-gray/30 bg-hz-gray/10 opacity-70"}`}>
                    <div className="flex items-start gap-3">
                      {c.photo_url && <PhotoThumb src={c.photo_url} alt={`Fotografía de ${c.full_name}`} className="h-14 w-14 rounded-xl object-cover" testId={`care-child-photo-${c.id}`} />}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-hz-ink">{c.full_name}{c.age != null ? ` · ${c.age} años` : ""}</p>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${c.care_status === "checked_in" ? "bg-hz-success/10 text-hz-success" : c.care_status === "checked_out" ? "bg-hz-gray/30 text-hz-ink" : "bg-hz-warning/10 text-hz-warning"}`} data-testid={`care-status-${c.id}`}>
                            {c.care_status === "checked_in" ? "Dentro" : c.care_status === "checked_out" ? "Entregado" : "No ingresó"}
                          </span>
                        </div>
                        {(c.allergies || c.medical_notes || c.special_needs) && (
                          <p className="mt-1 rounded-lg bg-hz-error/10 px-2 py-1 text-xs font-bold text-hz-error" data-testid={`care-child-alerts-${c.id}`}>
                            {[c.allergies && `Alergias: ${c.allergies}`, c.medical_notes && `Médico: ${c.medical_notes}`, c.special_needs && `Necesidades: ${c.special_needs}`].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        {mode === "check_out" && (
                          <p className="mt-1 text-xs text-hz-ink/60">
                            Autorizados: {(c.authorized_guardians || []).map((g) => g.name).join(", ") || "—"}
                          </p>
                        )}
                        {photoBlocked && (
                          <p className="mt-1 rounded-lg bg-hz-warning/10 px-2 py-1 text-xs font-bold text-hz-warning" data-testid={`care-photo-blocked-${c.id}`}>
                            Fotografía pendiente: acceso a cuidado infantil bloqueado.
                          </p>
                        )}
                        {photoBlocked && isManager && (
                          <ChildPhotoUpload childId={c.id} onDone={() => openGroup(ficha.group.id)} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {candidates.length === 0 && (
                <p className="rounded-xl border border-hz-gray/40 bg-white px-4 py-6 text-center text-sm text-hz-ink/60" data-testid="care-no-candidates">
                  {mode === "check_in" ? "Todos los niños de esta familia ya están dentro." : "No hay niños dentro del área para recoger."}
                </p>
              )}
            </div>

            {mode === "check_out" && selectedIds.length > 0 && (
              <div className="space-y-3 rounded-2xl border border-hz-gray/40 bg-white p-4">
                <div>
                  <Label className="text-sm font-semibold text-hz-ink">¿Quién recoge? *</Label>
                  <select value={receiver} onChange={(e) => setReceiver(e.target.value)} data-testid="care-receiver-select"
                    className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                    <option value="">Selecciona al adulto o tutor autorizado…</option>
                    {receiverOptions().map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </div>
                {isManager && (
                  <div>
                    <Label className="text-xs font-semibold text-hz-ink">Motivo de excepción (solo si la persona no está autorizada)</Label>
                    <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="care-override-input" className="mt-1" placeholder="Ej. Emergencia familiar verificada con el comité" />
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-hz-ink">Observaciones (opcional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="care-notes-input" className="mt-1" />
            </div>

            {feedback && (
              <div className={`space-y-1 rounded-xl border px-4 py-3 text-sm font-semibold ${feedback.ok ? "border-hz-success/30 bg-hz-success/5 text-hz-success" : "border-hz-error/30 bg-hz-error/5 text-hz-error"}`} data-testid="care-feedback" role="alert">
                {feedback.messages.map((m, i) => (
                  <p key={i} className="flex items-start gap-2">{feedback.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />} {m}</p>
                ))}
              </div>
            )}

            <Button onClick={submit} disabled={busy || selectedIds.length === 0 || (mode === "check_out" && !receiver)} data-testid="care-submit-btn"
              className={`w-full font-bold text-white disabled:opacity-60 ${mode === "check_in" ? "bg-hz-cyan hover:bg-hz-blue" : "bg-hz-navy hover:bg-hz-blue"}`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mode === "check_in" ? `Registrar entrada (${selectedIds.length})` : `Confirmar entrega (${selectedIds.length})`}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ChildCarePage;
